import { ReactNode, useState, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import {
  HeaderFiltersContext,
  type FilterConfig,
  type HeaderSearchConfig,
  type HeaderFiltersContextValue,
  type HeaderSlotName,
  type PathConfigs
} from './types';

const EMPTY_CONFIGS: PathConfigs[string] = { filterConfig: null, searchConfig: null, slots: {} };

interface HeaderFiltersProviderProps {
  children: ReactNode;
}

export function HeaderFiltersProvider({ children }: HeaderFiltersProviderProps) {
  const location = useLocation();
  const [pathConfigs, setPathConfigs] = useState<PathConfigs>({});
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const pathKey = location.pathname;

  const currentConfigs = useMemo(() => {
    return pathConfigs[pathKey] || EMPTY_CONFIGS;
  }, [pathConfigs, pathKey]);

  const setFilterConfig = useCallback(
    (config: FilterConfig | null) => {
      setPathConfigs((prev) => ({
        ...prev,
        [pathKey]: {
          ...(prev[pathKey] ?? EMPTY_CONFIGS),
          filterConfig: config
        }
      }));
    },
    [pathKey]
  );

  const setSearchConfig = useCallback(
    (config: HeaderSearchConfig | null) => {
      setPathConfigs((prev) => ({
        ...prev,
        [pathKey]: {
          ...(prev[pathKey] ?? EMPTY_CONFIGS),
          searchConfig: config
        }
      }));
    },
    [pathKey]
  );

  const setHeaderSlot = useCallback(
    (name: HeaderSlotName, node: ReactNode) => {
      setPathConfigs((prev) => ({
        ...prev,
        [pathKey]: {
          ...(prev[pathKey] ?? EMPTY_CONFIGS),
          slots: { ...(prev[pathKey]?.slots ?? {}), [name]: node }
        }
      }));
    },
    [pathKey]
  );

  const openFilters = useCallback(() => setIsFilterOpen(true), []);
  const closeFilters = useCallback(() => setIsFilterOpen(false), []);
  const toggleFilters = useCallback(() => setIsFilterOpen((prev) => !prev), []);

  const value: HeaderFiltersContextValue = {
    filterConfig: currentConfigs.filterConfig,
    searchConfig: currentConfigs.searchConfig,
    slots: currentConfigs.slots ?? EMPTY_CONFIGS.slots,
    isFilterOpen,
    setFilterConfig,
    setSearchConfig,
    setHeaderSlot,
    openFilters,
    closeFilters,
    toggleFilters
  };

  return <HeaderFiltersContext.Provider value={value}>{children}</HeaderFiltersContext.Provider>;
}
